import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Form,
  Input,
  Button,
  Alert,
  Progress,
  Steps,
  Table,
  Space,
  Switch,
  Modal,
  Divider,
  Tag,
  Row,
  Col,
  Statistic,
  message,
  Popconfirm
} from 'antd';
import {
  CloudSyncOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  EyeOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  LinkOutlined,
  StopOutlined
} from '@ant-design/icons';
import {
  startDomainMigration,
  previewMigration,
  getAllMigrations,
  getMigrationStatus,
  cancelMigration,
  clearMessages,
  updateFormData,
  resetForm,
  setActiveMigration,
  clearActiveMigration
} from '../../features/migration/domainMigrationSlice';

const { Step } = Steps;
const { TextArea } = Input;

const DomainMigration = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [selectedMigration, setSelectedMigration] = useState(null);

  const {
    currentMigration,
    migrations,
    pagination,
    preview,
    loading,
    previewLoading,
    migrationsLoading,
    error,
    success,
    formData,
    activeMigrationId
  } = useSelector((state) => state.domainMigration);

  useEffect(() => {
    dispatch(getAllMigrations());
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      message.success(success);
      dispatch(clearMessages());
    }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearMessages());
    }
  }, [error, dispatch]);

  // Polling for active migration status
  useEffect(() => {
    let interval;
    if (activeMigrationId) {
      interval = setInterval(() => {
        dispatch(getMigrationStatus(activeMigrationId));
      }, 2000); // Poll every 2 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeMigrationId, dispatch]);

  // Auto-clear active migration when completed
  useEffect(() => {
    if (currentMigration && ['completed', 'failed'].includes(currentMigration.status)) {
      setTimeout(() => {
        dispatch(clearActiveMigration());
      }, 5000); // Clear after 5 seconds
    }
  }, [currentMigration, dispatch]);

  // Update step based on migration status
  useEffect(() => {
    if (currentMigration) {
      switch (currentMigration.status) {
        case 'pending':
          setCurrentStep(1); // Execute step
          break;
        case 'running':
          setCurrentStep(1); // Execute step
          break;
        case 'completed':
          setCurrentStep(2); // Complete step
          break;
        case 'failed':
          setCurrentStep(2); // Complete step (even if failed)
          break;
        default:
          break;
      }
    } else {
      // No active migration, reset to configure step
      setCurrentStep(0);
    }
  }, [currentMigration]);

  const handleFormChange = (changedValues, allValues) => {
    dispatch(updateFormData(allValues));
  };

  const handlePreview = async () => {
    try {
      const values = await form.validateFields(['oldDomain', 'newDomain']);
      dispatch(previewMigration(values));
      setPreviewVisible(true);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleStartMigration = async () => {
    try {
      const values = await form.validateFields();
      dispatch(startDomainMigration(values));
      // Step will be updated by useEffect when currentMigration changes
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleCancelMigration = (migrationId) => {
    dispatch(cancelMigration(migrationId));
  };

  const handleViewDetails = (migration) => {
    setSelectedMigration(migration);
    setDetailsVisible(true);
  };

  const handleReset = () => {
    form.resetFields();
    dispatch(resetForm());
    setCurrentStep(0);
    setPreviewVisible(false);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'blue',
      running: 'orange',
      completed: 'green',
      failed: 'red'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <ClockCircleOutlined />,
      running: <PlayCircleOutlined />,
      completed: <CheckCircleOutlined />,
      failed: <ExclamationCircleOutlined />
    };
    return icons[status] || <ClockCircleOutlined />;
  };

  const migrationColumns = [
    {
      title: 'Migration',
      render: (_, record) => (
        <div>
          <div className="font-medium">
            <LinkOutlined className="mr-2" />
            {record.oldDomain} → {record.newDomain}
          </div>
          <div className="text-xs text-gray-500">
            {new Date(record.createdAt).toLocaleString()}
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Progress',
      dataIndex: 'progress',
      render: (progress, record) => (
        <div>
          <Progress 
            percent={progress} 
            size="small" 
            status={record.status === 'failed' ? 'exception' : 'normal'}
          />
          <div className="text-xs text-gray-500 mt-1">
            {record.statistics?.processedDocuments || 0} / {record.statistics?.totalDocuments || 0} documents
          </div>
        </div>
      ),
    },
    {
      title: 'Duration',
      render: (_, record) => {
        if (record.duration) {
          return `${record.duration}s`;
        }
        if (record.startedAt && record.status === 'running') {
          const elapsed = Math.round((new Date() - new Date(record.startedAt)) / 1000);
          return `${elapsed}s (running)`;
        }
        return '-';
      },
    },
    {
      title: 'Actions',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            Details
          </Button>
          {['pending', 'running'].includes(record.status) && (
            <Popconfirm
              title="Cancel Migration"
              description="Are you sure you want to cancel this migration?"
              onConfirm={() => handleCancelMigration(record._id)}
              okText="Yes"
              cancelText="No"
            >
              <Button size="small" danger icon={<StopOutlined />}>
                Cancel
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="shadow-soft border border-border-light rounded-xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <CloudSyncOutlined className="text-xl text-white" />
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-1">Domain Migration</h3>
              <p className="text-gray-600">Migrate media URLs from one domain to another</p>
            </div>
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => dispatch(getAllMigrations())}
            loading={migrationsLoading}
          >
            Refresh
          </Button>
        </div>
      </Card>

      {/* Current Migration Status */}
      {currentMigration && ['pending', 'running'].includes(currentMigration.status) && (
        <Card 
          title="Current Migration" 
          className="shadow-soft border border-border-light rounded-xl"
          extra={
            <Tag color={getStatusColor(currentMigration.status)} icon={getStatusIcon(currentMigration.status)}>
              {currentMigration.status.toUpperCase()}
            </Tag>
          }
        >
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="font-medium">
                {currentMigration.oldDomain} → {currentMigration.newDomain}
              </span>
              <span className="text-gray-500">
                {currentMigration.statistics?.processedDocuments || 0} / {currentMigration.statistics?.totalDocuments || 0} documents
              </span>
            </div>
            
            <Progress 
              percent={currentMigration.progress} 
              status={currentMigration.status === 'failed' ? 'exception' : 'normal'}
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
            />
            
            {currentMigration.estimatedCompletion && (
              <div className="text-sm text-gray-500">
                Estimated completion: {new Date(currentMigration.estimatedCompletion).toLocaleString()}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Migration Steps */}
      <Card className="shadow-soft border border-border-light rounded-xl">
        <Steps current={currentStep} className="mb-6">
          <Step title="Configure" description="Set migration parameters" />
          <Step title="Execute" description="Run the migration" />
          <Step title="Complete" description="Review results" />
        </Steps>

        {/* Step 0: Configure */}
        {currentStep === 0 && (
          <div className="space-y-6">
            <Alert
              message="Important Notice"
              description="Domain migration will update all media URLs in your database. It's recommended to backup your data before proceeding."
              type="warning"
              showIcon
              className="mb-6"
            />

            <Form
              form={form}
              layout="vertical"
              initialValues={formData}
              onValuesChange={handleFormChange}
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                  <Form.Item
                    label="Old Domain"
                    name="oldDomain"
                    rules={[
                      { required: true, message: 'Please enter the old domain' },
                      { type: 'url', message: 'Please enter a valid URL' }
                    ]}
                  >
                    <Input 
                      placeholder="https://old-domain.com" 
                      prefix={<LinkOutlined />}
                    />
                  </Form.Item>
                </Col>
                
                <Col xs={24} lg={12}>
                  <Form.Item
                    label="New Domain"
                    name="newDomain"
                    rules={[
                      { required: true, message: 'Please enter the new domain' },
                      { type: 'url', message: 'Please enter a valid URL' }
                    ]}
                  >
                    <Input 
                      placeholder="https://new-domain.com" 
                      prefix={<LinkOutlined />}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Divider>Migration Options</Divider>

              <Row gutter={[16, 16]}>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Dry Run"
                    name={['options', 'dryRun']}
                    valuePropName="checked"
                  >
                    <Switch 
                      checkedChildren="On" 
                      unCheckedChildren="Off"
                    />
                  </Form.Item>
                  <p className="text-xs text-gray-500">Preview changes without applying them</p>
                </Col>
                
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Backup Before Migration"
                    name={['options', 'backupBeforeMigration']}
                    valuePropName="checked"
                  >
                    <Switch 
                      checkedChildren="On" 
                      unCheckedChildren="Off"
                      defaultChecked
                    />
                  </Form.Item>
                  <p className="text-xs text-gray-500">Create backup before starting</p>
                </Col>
                
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Validate URLs"
                    name={['options', 'validateUrls']}
                    valuePropName="checked"
                  >
                    <Switch 
                      checkedChildren="On" 
                      unCheckedChildren="Off"
                    />
                  </Form.Item>
                  <p className="text-xs text-gray-500">Check if new URLs are accessible</p>
                </Col>
              </Row>

              <div className="flex justify-between items-center pt-6">
                <Button onClick={handleReset}>
                  Reset
                </Button>
                <Space>
                  <Button 
                    icon={<EyeOutlined />}
                    onClick={handlePreview}
                    loading={previewLoading}
                  >
                    Preview Changes
                  </Button>
                  <Button 
                    type="primary"
                    icon={<PlayCircleOutlined />}
                    onClick={handleStartMigration}
                    loading={loading}
                  >
                    Start Migration
                  </Button>
                </Space>
              </div>
            </Form>
          </div>
        )}

        {/* Step 1: Execute (Migration in progress) */}
        {currentStep === 1 && currentMigration && (
          <div className="space-y-6">
            <Alert
              message={currentMigration.status === 'pending' ? 'Migration Queued' : 'Migration in Progress'}
              description={
                currentMigration.status === 'pending' 
                  ? 'Your migration is queued and will start shortly.'
                  : 'Please wait while the migration is being processed. Do not close this page.'
              }
              type="info"
              showIcon
            />

            <div className="text-center space-y-4">
              <div className="text-lg font-medium">
                Migrating from {currentMigration.oldDomain} to {currentMigration.newDomain}
              </div>
              
              <Progress
                type="circle"
                percent={currentMigration.progress}
                status={currentMigration.status === 'failed' ? 'exception' : 'active'}
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
                size={120}
              />
              
              <div className="text-gray-600">
                {currentMigration.statistics?.processedDocuments || 0} / {currentMigration.statistics?.totalDocuments || 0} documents processed
              </div>
              
              {currentMigration.estimatedCompletion && (
                <div className="text-sm text-gray-500">
                  Estimated completion: {new Date(currentMigration.estimatedCompletion).toLocaleString()}
                </div>
              )}
            </div>

            <div className="flex justify-center">
              <Popconfirm
                title="Cancel Migration"
                description="Are you sure you want to cancel this migration? This action cannot be undone."
                onConfirm={() => handleCancelMigration(currentMigration._id)}
                okText="Yes, Cancel"
                cancelText="No"
              >
                <Button danger icon={<StopOutlined />}>
                  Cancel Migration
                </Button>
              </Popconfirm>
            </div>
          </div>
        )}

        {/* Step 2: Complete */}
        {currentStep === 2 && currentMigration && (
          <div className="space-y-6">
            {currentMigration.status === 'completed' ? (
              <Alert
                message="Migration Completed Successfully!"
                description={`Your domain migration from ${currentMigration.oldDomain} to ${currentMigration.newDomain} has been completed successfully.`}
                type="success"
                showIcon
              />
            ) : (
              <Alert
                message="Migration Failed"
                description={`Your domain migration from ${currentMigration.oldDomain} to ${currentMigration.newDomain} has failed. Please check the error details below.`}
                type="error"
                showIcon
              />
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card size="small">
                <Statistic
                  title="Total Documents"
                  value={currentMigration.statistics?.totalDocuments || 0}
                  prefix={<DatabaseOutlined />}
                />
              </Card>
              <Card size="small">
                <Statistic
                  title="Processed"
                  value={currentMigration.statistics?.processedDocuments || 0}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
              <Card size="small">
                <Statistic
                  title="Updated"
                  value={currentMigration.statistics?.updatedDocuments || 0}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
              <Card size="small">
                <Statistic
                  title="Duration"
                  value={currentMigration.duration || 0}
                  suffix="s"
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </div>

            <div className="flex justify-center space-x-4">
              <Button 
                type="primary" 
                onClick={handleReset}
                icon={<ReloadOutlined />}
              >
                Start New Migration
              </Button>
              <Button 
                onClick={() => handleViewDetails(currentMigration)}
                icon={<EyeOutlined />}
              >
                View Details
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Migration History */}
      <Card 
        title="Migration History"
        className="shadow-soft border border-border-light rounded-xl"
        extra={
          <span className="text-sm text-gray-500">
            {pagination.totalMigrations} total migrations
          </span>
        }
      >
        <Table
          dataSource={migrations}
          columns={migrationColumns}
          loading={migrationsLoading}
          rowKey="_id"
          pagination={{
            current: pagination.currentPage,
            total: pagination.totalMigrations,
            pageSize: pagination.limit,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} migrations`,
          }}
        />
      </Card>

      {/* Preview Modal */}
      <Modal
        title="Migration Preview"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            Close
          </Button>
        ]}
      >
        {preview && (
          <div className="space-y-4">
            <Alert
              message={`${preview.totalDocuments} documents will be affected`}
              type="info"
              showIcon
            />
            
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(preview.affectedCollections).map(([collection, data]) => (
                <Card key={collection} size="small">
                  <Statistic
                    title={collection.charAt(0).toUpperCase() + collection.slice(1)}
                    value={data.totalDocuments}
                    prefix={<DatabaseOutlined />}
                  />
                </Card>
              ))}
            </div>
            
            {preview.sampleChanges.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Sample Changes:</h4>
                {preview.sampleChanges.slice(0, 5).map((change, index) => (
                  <div key={index} className="bg-gray-50 p-3 rounded mb-2">
                    <div className="text-sm font-medium text-gray-600 mb-1">
                      {change.collection} - {change.field}
                    </div>
                    <div className="text-xs">
                      <div className="text-red-600">- {change.oldValue}</div>
                      <div className="text-green-600">+ {change.newValue}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Details Modal */}
      <Modal
        title="Migration Details"
        open={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        width={1000}
        footer={[
          <Button key="close" onClick={() => setDetailsVisible(false)}>
            Close
          </Button>
        ]}
      >
        {selectedMigration && (
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium mb-2">Migration Info</h4>
                <div className="space-y-2 text-sm">
                  <div><strong>From:</strong> {selectedMigration.oldDomain}</div>
                  <div><strong>To:</strong> {selectedMigration.newDomain}</div>
                  <div><strong>Status:</strong> 
                    <Tag color={getStatusColor(selectedMigration.status)} className="ml-2">
                      {selectedMigration.status}
                    </Tag>
                  </div>
                  <div><strong>Progress:</strong> {selectedMigration.progress}%</div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">Statistics</h4>
                <Row gutter={[8, 8]}>
                  <Col span={12}>
                    <Statistic
                      title="Total"
                      value={selectedMigration.statistics?.totalDocuments || 0}
                      size="small"
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Updated"
                      value={selectedMigration.statistics?.updatedDocuments || 0}
                      size="small"
                    />
                  </Col>
                </Row>
              </div>
            </div>

            {/* Collection Stats */}
            {selectedMigration.statistics?.collections && (
              <div>
                <h4 className="font-medium mb-3">Collection Statistics</h4>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(selectedMigration.statistics.collections).map(([collection, stats]) => (
                    <Card key={collection} size="small">
                      <div className="text-center">
                        <div className="font-medium capitalize mb-2">{collection}</div>
                        <Row gutter={[8, 8]}>
                          <Col span={8}>
                            <Statistic title="Total" value={stats.total} size="small" />
                          </Col>
                          <Col span={8}>
                            <Statistic title="Updated" value={stats.updated} size="small" />
                          </Col>
                          <Col span={8}>
                            <Statistic title="Failed" value={stats.failed} size="small" />
                          </Col>
                        </Row>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Errors */}
            {selectedMigration.errors && selectedMigration.errors.length > 0 && (
              <div>
                <h4 className="font-medium mb-3 text-red-600">Errors ({selectedMigration.errors.length})</h4>
                <div className="max-h-40 overflow-y-auto space-y-2">
                  {selectedMigration.errors.slice(0, 10).map((error, index) => (
                    <div key={index} className="bg-red-50 border border-red-200 rounded p-2 text-sm">
                      <div className="font-medium text-red-800">{error.collection} - {error.documentId}</div>
                      <div className="text-red-600">{error.error}</div>
                      <div className="text-red-500 text-xs">{new Date(error.timestamp).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DomainMigration;